import { useState, useCallback } from 'react'
import { coversApi } from '../services/api'

export interface AiModel {
  id: string
  name: string
  description: string
  cost: number
}

export const AI_MODELS: AiModel[] = [
  {
    id: 'gemini-2.5-flash-image',
    name: 'Gemini 2.5 Flash',
    description: '빠르고 효율적',
    cost: 39
  },
  {
    id: 'gemini-3-pro-image-preview',
    name: 'Gemini 3 Pro',
    description: '4K 지원, 최고 품질',
    cost: 120
  },
  {
    id: 'gemini-2.0-flash-image',
    name: 'Gemini 2.0 Flash',
    description: '균형잡힌 성능',
    cost: 39
  },
  {
    id: 'imagen-3',
    name: 'Imagen 3',
    description: '구글 이미지 생성 전용',
    cost: 30
  },
  {
    id: 'dall-e-3',
    name: 'DALL-E 3',
    description: 'OpenAI 고품질',
    cost: 80
  },
]

interface UseCoverAiGenerationProps {
  coverId: string
  coverWidth: number
  coverHeight: number
  onGenerationComplete?: (assetId: string) => void
}

export function useCoverAiGeneration({
  coverId,
  coverWidth,
  coverHeight,
  onGenerationComplete,
}: UseCoverAiGenerationProps) {
  // Model selection
  const [selectedModel, setSelectedModel] = useState<string>('gemini-2.5-flash-image')

  // Asset selection for reference
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([])

  // Prompt state
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('')
  const [customInstructions, setCustomInstructions] = useState<string>('')

  // Loading states
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false)
  const [isGeneratingCover, setIsGeneratingCover] = useState(false)

  // Result state
  const [generatedAssetId, setGeneratedAssetId] = useState<string | null>(null)
  const [generationCost, setGenerationCost] = useState<number>(0)
  const [pointsRemaining, setPointsRemaining] = useState<number>(0)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // Toggle asset selection
  const toggleAssetSelection = useCallback((assetId: string) => {
    setSelectedAssetIds(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    )
  }, [])

  // Clear asset selection
  const clearAssetSelection = useCallback(() => {
    setSelectedAssetIds([])
  }, [])

  // Generate prompt from selected assets
  const generatePrompt = useCallback(async () => {
    if (selectedAssetIds.length === 0) {
      setError('에셋을 선택해주세요.')
      return
    }

    setIsGeneratingPrompt(true)
    setError(null)

    try {
      const result = await coversApi.generatePrompt(coverId, {
        assetIds: selectedAssetIds,
        coverWidth,
        coverHeight,
        customInstructions: customInstructions.trim() || undefined,
      })

      if (result.success && result.data) {
        setGeneratedPrompt(result.data.prompt)
      } else {
        setError(result.error?.message || '프롬프트 생성에 실패했습니다.')
      }
    } catch (err: any) {
      setError(err.message || '프롬프트 생성 중 오류가 발생했습니다.')
    } finally {
      setIsGeneratingPrompt(false)
    }
  }, [coverId, selectedAssetIds, coverWidth, coverHeight, customInstructions])

  // Generate cover image
  const generateCover = useCallback(async () => {
    if (!generatedPrompt.trim()) {
      setError('프롬프트를 입력하거나 생성해주세요.')
      return
    }

    setIsGeneratingCover(true)
    setError(null)

    try {
      const aspectRatio = `${coverWidth}:${coverHeight}`

      const result = await coversApi.generateCover(coverId, {
        model: selectedModel,
        prompt: generatedPrompt,
        referenceAssetIds: selectedAssetIds.length > 0 ? selectedAssetIds : undefined,
        aspectRatio,
      })

      if (result.success && result.data) {
        setGeneratedAssetId(result.data.assetId)
        setGenerationCost(result.data.cost)
        setPointsRemaining(result.data.pointsRemaining)

        if (onGenerationComplete) {
          onGenerationComplete(result.data.assetId)
        }
      } else {
        if (result.error?.code === 'INSUFFICIENT_POINTS') {
          const details = result.error.details as any
          setError(`포인트가 부족합니다. (필요: ${details?.required}P, 보유: ${details?.current}P)`)
        } else {
          setError(result.error?.message || '표지 생성에 실패했습니다.')
        }
      }
    } catch (err: any) {
      setError(err.message || '표지 생성 중 오류가 발생했습니다.')
    } finally {
      setIsGeneratingCover(false)
    }
  }, [coverId, selectedModel, generatedPrompt, selectedAssetIds, coverWidth, coverHeight, onGenerationComplete])

  // Reset all state
  const reset = useCallback(() => {
    setSelectedAssetIds([])
    setGeneratedPrompt('')
    setCustomInstructions('')
    setGeneratedAssetId(null)
    setGenerationCost(0)
    setError(null)
  }, [])

  // Get selected model info
  const selectedModelInfo = AI_MODELS.find(m => m.id === selectedModel) || AI_MODELS[0]

  return {
    // Model selection
    selectedModel,
    setSelectedModel,
    selectedModelInfo,

    // Asset selection
    selectedAssetIds,
    toggleAssetSelection,
    clearAssetSelection,

    // Prompt
    generatedPrompt,
    setGeneratedPrompt,
    customInstructions,
    setCustomInstructions,

    // Actions
    generatePrompt,
    generateCover,
    reset,

    // State
    isGeneratingPrompt,
    isGeneratingCover,
    generatedAssetId,
    generationCost,
    pointsRemaining,
    error,
    setError,
  }
}
